import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "@/hooks";
import { getCurrentProvider, switchProvider } from "@/lib/ai/providers/openai";
import {
  FREE_MODELS,
  MODEL_PROVIDERS,
  PREMIUM_MODELS,
} from "@/lib/constants/app";
import { getEnabledModels, sortModelsWithPinnedFirst } from "@/lib/utils/ai";
import { cn } from "@/lib/utils";
import { useAssistantStore, useAuthStore } from "@/store";
import useModelStore from "@/store/modelStore";
import { Crown } from "lucide-react";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TbPinFilled } from "react-icons/tb";
import { useTouchDevice } from "@/hooks/useTouchDevice";
import { ClaudeIcon } from "./icons/ClaudeIcon";
import { CohereIcon } from "./icons/CohereIcon";
import { DeepSeekIcon } from "./icons/DeepSeekIcon";
import { GeminiIcon } from "./icons/GeminiIcon";
import { GrokIcon } from "./icons/GrokIcon";
import { GroqIcon } from "./icons/GroqIcon";
import { MistralIcon } from "./icons/MistralIcon";
import { OpenAIIcon } from "./icons/OpenAIIcon";
import { OpenRouterIcon } from "./icons/OpenRouterIcon";
import { PerplexityIcon } from "./icons/PerplexityIcon";
import { QwenIcon } from "./icons/Qwen";

// Add model capabilities database
const MODEL_CAPABILITIES = {
  // OpenAI Models
  "gpt-4.1": "Great for quick coding and analysis",
  "gpt-4.1-mini": "Fast and efficient for lightweight tasks",
  "gpt-4.1-nano": "Ultra-fast responses for simple tasks",
  "gpt-4o": "Great for most tasks",
  "gpt-4o-mini": "Fast and cost-effective for most tasks",
  "gpt-5": "Advanced reasoning and complex problem-solving",
  "gpt-5-mini": "Balanced performance with improved speed",
  "gpt-5-nano": "Lightweight model for quick responses",
  "gpt-5.1": "Enhanced capabilities for coding and analysis",
  "gpt-5.1-codex-mini": "Specialized for code generation and debugging",
  "gpt-5.1-codex": "Advanced code generation and software development",
  "gpt-5.2": "Next-generation reasoning with enhanced multimodal capabilities",

  // Claude Models
  "claude-opus-4-20250514":
    "Most powerful model for complex reasoning and coding",
  "claude-sonnet-4-20250514":
    "High-performance model with exceptional reasoning",
  "claude-sonnet-4-5-20250929":
    "Smart model for complex agents and coding tasks",
  "claude-3-7-sonnet-20250219":
    "High intelligence with toggleable extended thinking",
  "claude-3-5-haiku-20241022":
    "Quick, intelligent responses with improved reasoning",

  // Gemini Models
  "gemini-2.5-pro": "Advanced reasoning, coding, and complex analysis tasks",
  "gemini-2.5-flash":
    "Hybrid reasoning model with controllable thinking capabilities",
  "gemini-2.5-flash-lite":
    "Fastest and most cost-efficient multimodal model for high-frequency tasks",
  "gemini-3-pro-preview":
    "Preview of Gemini 3 Pro with the richest multimodal understanding available",

  // DeepSeek Models
  "deepseek-r1": "Advanced reasoning, mathematics, and complex problem-solving",
  "deepseek-v3":
    "General intelligence with strong coding and reasoning capabilities",
  "deepseek-coder":
    "Specialized for code generation, debugging, and programming",
  "deepseek-chat":
    "Cost-effective training, competitive coding and mathematics",
  "deepseek-reasoner":
    "Transparent chain-of-thought reasoning, self-verification capabilities",

  // Mistral Models
  "mistral-large-2402": "Large-scale reasoning, coding, and multilingual tasks",
  "mistral-medium":
    "Balanced performance for various professional applications",
  "mistral-small": "Efficient model for lightweight tasks and conversations",
  "mistral-large-latest":
    "Latest large-scale reasoning, coding, and multilingual tasks",
  "codestral-latest": "Code generation, completion, and programming assistance",
  "mistral-7b-instruct": "Instruction following and general conversation",
  "mistral-small-latest":
    "State-of-the-art performance with multimodal understanding",
  "open-mistral-nemo": "Multilingual capabilities and efficient reasoning",

  // Groq Models
  "llama-3.1-405b-reasoning":
    "Large-scale reasoning and complex problem-solving",
  "llama-3.1-70b-versatile":
    "Versatile model for various tasks and applications",
  "llama-3.1-8b-instant": "Fast responses for lightweight tasks",
  "llama-3.2-90b-text-preview": "Advanced text generation and reasoning",
  "llama-3.2-11b-text-preview": "Efficient text processing and generation",
  "llama-3.2-3b-preview": "Lightweight model for basic tasks",
  "llama-3.2-1b-preview": "Ultra-lightweight for simple conversations",
  "mixtral-8x7b-32768": "Mixture of experts for diverse task handling",
  "gemma-7b-it": "Instruction tuned for helpful and harmless responses",
  "gemma2-9b-it": "Improved instruction following and reasoning",
  "llama-3.3-70b-versatile": "Latest Llama 3.3 model with enhanced versatility",
  "moonshotai/kimi-k2-instruct-0905":
    "Moonshot AI's Kimi K2 instruction-tuned model",

  // XAI Models
  "grok-4-0709": "Next-generation reasoning with enhanced capabilities",
  "grok-code-fast-1":
    "A speedy and economical reasoning model that excels at agentic coding.",
  "grok-3": "Superior reasoning with advanced mathematics capabilities",
  "grok-3-fast": "Low latency for time-sensitive applications",
  "grok-3-mini": "Cost-efficient reasoning for STEM-focused tasks",
  "grok-3-mini-fast": "Rapid response generation for real-time reasoning",

  // Cohere Models
  "command-a-03-2025":
    "Exceptional tool use, RAG, and multilingual performance",
  "command-r7b-12-2024": "High throughput and latency-sensitive applications",
  "command-r-plus-08-2024":
    "Advanced multi-step tool use, complex RAG functionality",
  "command-r-08-2024": "High precision on RAG and tool use",
  "command-nightly": "Latest experimental features and improvements",

  // Perplexity Models
  "llama-3.1-sonar-small-128k-online":
    "Web search integration with real-time information",
  "llama-3.1-sonar-large-128k-online":
    "Advanced web search and information synthesis",
  "llama-3.1-sonar-huge-128k-online": "Comprehensive web research and analysis",
  sonar: "Real-time web search with speed optimization",
  "sonar-pro": "Deep content understanding with 2x more citations",
  "sonar-deep-research":
    "Exhaustive multi-source research, expert-level analysis",

  // Qwen Models
  "qwen-max-latest":
    "Most powerful Qwen model with superior reasoning and complex analysis capabilities",
  "qwen-flash":
    "Fast and efficient model optimized for quick responses and real-time applications",
  "qwen-turbo-latest":
    "High-speed optimized model for fast responses and high QPS",
  "qwen3-235b-a22b-instruct":
    "Qwen 3 flagship model with MoE architecture and 32 experts",
  "qwen3-235b-a22b-thinking-2507":
    "Qwen 3 thinking model with extended reasoning capabilities",
  "qwen3-32b-instruct":
    "Latest Qwen 3 efficient model with multilingual support and embedding generation",

  // OpenRouter Models
  "liquid/lfm-7b":
    "Best-in-class 7B multilingual model with exceptional chat capabilities in English, Arabic, and Japanese",

  // Default fallback
  default: "General purpose AI assistant",
};

export const ModelSwitcher = ({ className, zIndex = 300 }) => {
  const { toast } = useToast();
  const _modelStore = useModelStore();
  const setProvider = useModelStore((state) => state.setProvider);
  const setModel = useModelStore((state) => state.setModel);
  const localPinnedModels = useModelStore((state) => state.pinnedModels);
  const { isPremiumUser, user, profile } = useAuthStore((state) => state);
  const { currentAssistant, updateAssistant } = useAssistantStore();

  // Detect touch devices to use native select for better scroll experience
  const { isTouchDevice } = useTouchDevice();

  // Get pinned models - from profile for authenticated users, localStorage for unauthenticated
  const pinnedModels = user
    ? profile?.settings?.pinnedModels || []
    : localPinnedModels || [];

  // Optimistic UI state
  const [optimisticModel, setOptimisticModel] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const scrollAreaRef = useRef(null);

  // Initialize provider on component mount based on current model
  useEffect(() => {
    if (currentAssistant?.model) {
      // Get the expected provider for this model
      const expectedProvider =
        MODEL_PROVIDERS[currentAssistant.model] || "openai";
      // Get the current active provider
      const activeProvider = getCurrentProvider();

      // If there's a mismatch, switch to the correct provider
      if (expectedProvider !== activeProvider) {
        switchProvider(currentAssistant.model);

        // Also update the model store
        setProvider(expectedProvider);
        setModel(currentAssistant.model);
      }
    }
  }, [currentAssistant?.model, setProvider, setModel]);

  // Reset optimistic state when assistant model changes
  useEffect(() => {
    if (currentAssistant?.model && optimisticModel && !isUpdating) {
      setOptimisticModel(null);
    }
  }, [currentAssistant?.model, optimisticModel, isUpdating]);

  // For non-logged-in users, create a mock assistant with gpt-4.1-mini
  // For logged-in users without an assistant, also default to gpt-4.1-mini
  const effectiveAssistant = !user
    ? { model: "gpt-4.1-mini", id: "anonymous" }
    : currentAssistant || { model: "gpt-4.1-mini", id: "default" };

  // Always show the model switcher with gpt-4.1-mini as fallback
  if (!effectiveAssistant) {
    console.warn("No current assistant found in store, using default");
  }

  // Get user's model preferences from profile settings
  const userModelPreferences = profile?.settings?.models || {};

  // Filter models based on user preferences - only show enabled models
  const enabledModels = getEnabledModels(user, userModelPreferences);

  // Ensure at least one model is always available (fallback to gpt-4.1-mini)
  const finalEnabledModels =
    enabledModels.length > 0 ? enabledModels : ["gpt-4.1-mini"];

  // Get model options from enabled models only
  const modelOptions = finalEnabledModels.map((modelId) => ({
    value: modelId,
    label: modelId,
    isPremium: PREMIUM_MODELS.includes(modelId),
    isFree: true,
    provider: MODEL_PROVIDERS[modelId] || "openai",
  }));

  // If the current model is not in the enabled list, we should still show it
  // but mark it as disabled in preferences
  const currentModel = optimisticModel || effectiveAssistant?.model;
  if (currentModel && !finalEnabledModels.includes(currentModel) && user) {
    // Add the current model to the options so user can see what's selected
    // but they won't be able to switch back to it once they change
    const currentModelOption = {
      value: currentModel,
      label: currentModel,
      isPremium: PREMIUM_MODELS.includes(currentModel),
      isFree: FREE_MODELS.includes(currentModel),
      provider: MODEL_PROVIDERS[currentModel] || "openai",
      isDisabledInPreferences: true,
    };

    // Add it to the beginning of the list
    modelOptions.unshift(currentModelOption);
  }

  // Sort models with pinned models first
  const sortedModelOptions = useMemo(() => {
    return sortModelsWithPinnedFirst(modelOptions, pinnedModels);
  }, [modelOptions, pinnedModels]);

  // Handle select open change and scroll to top when opening
  const handleOpenChange = useCallback((open) => {
    setIsSelectOpen(open);
    if (open) {
      // Use setTimeout to ensure the scroll area is rendered before scrolling
      setTimeout(() => {
        if (scrollAreaRef.current) {
          const viewport = scrollAreaRef.current.querySelector(
            "[data-radix-scroll-area-viewport]"
          );
          if (viewport) {
            viewport.scrollTop = 0;
          }
        }
      }, 0);
    }
  }, []);

  const handleModelChange = async (newValue) => {
    // For non-logged-in users, don't allow model changes
    if (!user) {
      toast({
        title: "Sign in required",
        description: "Please sign in to change models",
        variant: "destructive",
      });
      return;
    }

    // If no assistant exists, we can't update it, but we should still update the model store
    if (!currentAssistant) {
      console.warn("No current assistant found, updating model store only");
      // Still update the model store for consistency
      setModel(newValue);
      switchProvider(newValue);
      return;
    }

    // Check if the model is disabled in user preferences
    const selectedOption = modelOptions.find((m) => m.value === newValue);
    if (selectedOption?.isDisabledInPreferences) {
      toast({
        title: "Model disabled",
        description:
          "This model is disabled in your preferences. Enable it in Settings > Models to use it.",
        variant: "destructive",
      });
      return;
    }

    // Check if it's a premium model and user doesn't have premium access
    if (selectedOption?.isPremium && !isPremiumUser) {
      toast({
        title: "Premium model",
        description: "This model requires a premium subscription to use.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Only allow selection if model is free or user is premium
      const model = modelOptions.find((m) => m.value === newValue);
      if (model && (model.isFree || (model.isPremium && isPremiumUser))) {
        // Set optimistic state immediately
        setOptimisticModel(newValue);
        setIsUpdating(true);

        // Get the current provider before switching
        const beforeProvider = getCurrentProvider();

        // Check if we're switching to a different provider
        const isDeepseekModel = newValue.includes("deepseek");
        const wasDeepseekModel = currentAssistant.model?.includes("deepseek");
        const isGeminiModel = newValue.includes("gemini");
        const wasGeminiModel = currentAssistant.model?.includes("gemini");
        const isMistralModel =
          newValue.includes("mistral") && !newValue.includes("mixtral");
        const wasMistralModel =
          currentAssistant.model?.includes("mistral") &&
          !currentAssistant.model?.includes("mixtral");
        const isAnthropicModel = newValue.includes("claude");
        const wasAnthropicModel = currentAssistant.model?.includes("claude");
        const isGroqModel =
          newValue.includes("gemma") ||
          newValue.includes("llama") ||
          newValue.includes("mixtral") ||
          (newValue.includes("mistral") && !isMistralModel);
        const wasGroqModel =
          currentAssistant.model?.includes("gemma") ||
          currentAssistant.model?.includes("llama") ||
          currentAssistant.model?.includes("mixtral") ||
          (currentAssistant.model?.includes("mistral") && !wasMistralModel);
        const isXAIModel = newValue.includes("grok");
        const wasXAIModel = currentAssistant.model?.includes("grok");
        const isCohereModel = newValue.includes("command");
        const wasCohereModel = currentAssistant.model?.includes("command");
        const isPerplexityModel =
          newValue.includes("sonar") || newValue.includes("r1-1776");
        const wasPerplexityModel =
          currentAssistant.model?.includes("sonar") ||
          currentAssistant.model?.includes("r1-1776");

        const _isProviderSwitch =
          isDeepseekModel !== wasDeepseekModel ||
          isGeminiModel !== wasGeminiModel ||
          isMistralModel !== wasMistralModel ||
          isAnthropicModel !== wasAnthropicModel ||
          isGroqModel !== wasGroqModel ||
          isXAIModel !== wasXAIModel ||
          isCohereModel !== wasCohereModel ||
          isPerplexityModel !== wasPerplexityModel;

        // Always switch the provider internally
        switchProvider(newValue);

        // Get the provider after switching
        const afterProvider = getCurrentProvider();

        // Log provider change
        if (beforeProvider !== afterProvider) {
          // Update the model store's provider value
          setProvider(afterProvider);
        }

        // Always update the model in the store
        setModel(newValue);

        try {
          // Update the assistant with the new model
          await updateAssistant(currentAssistant.id, {
            ...currentAssistant,
            model: newValue,
            metadata: {
              ...currentAssistant.metadata,
              provider: afterProvider,
              model_provider: afterProvider,
            },
          });

          // Clear optimistic state on success
          setOptimisticModel(null);
        } catch (updateError) {
          console.error("Error updating assistant:", updateError);

          // Revert optimistic state on error
          setOptimisticModel(null);

          // Revert model store changes
          setModel(currentAssistant.model);
          switchProvider(currentAssistant.model);
          setProvider(beforeProvider);

          toast({
            title: "Failed to update model",
            description: updateError.message,
            variant: "destructive",
          });
        } finally {
          setIsUpdating(false);
        }

        // Show appropriate toast message
        // if (isProviderSwitch) {
        //   toast({
        //     title: "Model and provider updated",
        //     description: `Switched to ${newValue} (${afterProvider} provider)`,
        //   });
        // } else {
        //   toast({
        //     title: "Model updated",
        //     description: `Switched to ${newValue}`,
        //   });
        // }
      }
    } catch (error) {
      console.error("Error in model change:", error);
      setOptimisticModel(null);
      setIsUpdating(false);
      toast({
        title: "Failed to update model",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const getProviderIcon = (provider) => {
    switch (provider) {
      case "deepseek":
        return (
          <DeepSeekIcon
            size={14}
            className="text-[#015482] dark:text-[#015482]"
          />
        );
      case "gemini":
        return (
          <GeminiIcon
            size={14}
            className="text-[#1967D2] dark:text-[#1967D2]"
          />
        );
      case "mistral":
        return (
          <MistralIcon
            size={14}
            className="text-orange-600 dark:text-orange-400"
          />
        );
      case "anthropic":
        return <ClaudeIcon size={14} className="fill-[#D97757]" />;
      case "groq":
        return (
          <GroqIcon size={14} className="text-green-600 dark:text-green-400" />
        );
      case "xai":
        return (
          <GrokIcon size={14} className="text-blue-400 dark:text-blue-200" />
        );
      case "cohere":
        return (
          <CohereIcon size={14} className="text-teal-600 dark:text-teal-400" />
        );
      case "perplexity":
        return (
          <PerplexityIcon
            size={14}
            className="text-red-600 dark:text-red-400"
          />
        );
      case "qwen":
        return (
          <QwenIcon
            size={14}
            className="text-purple-600 dark:text-purple-400"
          />
        );
      case "openrouter":
        return (
          <OpenRouterIcon
            size={14}
            className="text-indigo-600 dark:text-indigo-400"
          />
        );
      case "openai":
      default:
        return (
          <OpenAIIcon
            size={14}
            className="fill-black dark:fill-white text-black dark:text-white"
          />
        );
    }
  };

  const IconWrapper = ({ className: iconClassName, provider = "openai" }) => {
    // Use a transparent background with a more visible border
    const borderStyle = "border border-gray-300 dark:border-gray-500";

    return (
      <div
        className={cn(
          "w-6 h-6 rounded-full flex items-center justify-center",
          "bg-transparent",
          borderStyle,
          iconClassName
        )}
      >
        {getProviderIcon(provider)}
      </div>
    );
  };

  const CustomSelectValue = ({ value, ...props }) => {
    const selectedModel = modelOptions?.find((m) => m.value === value);
    // Format a shorter display name for the model
    const getDisplayName = (modelName) => {
      // Model name display mappings (for cleaner UI display)
      const displayNameMap = {
        "gemini-3-pro-preview": "gemini-3-pro",
        "moonshotai/kimi-k2-instruct-0905": "kimi-k2-instruct-0905",
      };
      if (displayNameMap[modelName]) {
        return displayNameMap[modelName];
      }

      // Handle OpenRouter models with provider/model format
      if (modelName.includes("/")) {
        const parts = modelName.split("/");
        if (parts.length === 2) {
          const modelPart = parts[1];
          if (modelPart.includes("hermes")) {
            return "Hermes " + modelPart.split("-").slice(-2, -1).join(" ");
          }
          // Default shortened name
          return modelPart.split("-")[0];
        }
        return modelName.split("/")[1]; // Fall back to right part of the slash
      }

      // Remove various date patterns from model names
      let cleanName = modelName;

      // Handle command models first to preserve variants like -a, -r7b
      // command-a-03-2025 -> command-a, command-r7b-12-2024 -> command-r7b
      cleanName = cleanName.replace(/(command-[a-z0-9]+)-\d{2}-\d{4}$/, "$1");

      // Remove date patterns like -20250219, -20241022, etc. (YYYYMMDD format)
      cleanName = cleanName.replace(/-\d{8}$/, "");

      // Remove date patterns like -2024-05-13, -2025-02-19, etc. (YYYY-MM-DD format)
      cleanName = cleanName.replace(/-\d{4}-\d{2}-\d{2}$/, "");

      // Remove date patterns like -20241022, -20250219 in the middle or end
      cleanName = cleanName.replace(/-\d{8}(?=-|$)/, "");

      // Remove timestamp patterns like -preview-05-06
      cleanName = cleanName.replace(/-preview-\d{2}-\d{2}$/, "");

      // Remove other timestamp-like patterns
      cleanName = cleanName.replace(/-\d{4}\d{4}$/, "");

      // Remove general patterns like -03-2025, -12-2024 (MM-YYYY format) - but only if not already handled by command pattern
      cleanName = cleanName.replace(/-\d{2}-\d{4}$/, "");

      // If it's a model with "mini" in the name, shorten it
      if (cleanName.includes("-mini-")) {
        cleanName = cleanName.split("-mini-")[0] + "-mini";
      }

      return cleanName;
    };

    return (
      <SelectValue {...props}>
        <span className="truncate min-w-0 block">
          {selectedModel ? getDisplayName(selectedModel.label) : "Select model"}
        </span>
      </SelectValue>
    );
  };

  // Helper function to get display name for model labels
  const getModelDisplayName = (label) => {
    const displayNameMap = {
      "gemini-3-pro-preview": "gemini-3-pro",
      "moonshotai/kimi-k2-instruct-0905": "kimi-k2-instruct-0905",
    };
    return displayNameMap[label] || label;
  };

  const ModelLabel = ({ label, provider = "openai", showIcon = true }) => {
    // Model name display mappings (for cleaner UI display)
    const displayNameMap = {
      "gemini-3-pro-preview": "gemini-3-pro",
      "moonshotai/kimi-k2-instruct-0905": "kimi-k2-instruct-0905",
    };
    if (displayNameMap[label]) {
      return (
        <div className="flex items-center gap-2">
          {showIcon && <IconWrapper provider={provider} />}
          <span>{displayNameMap[label]}</span>
        </div>
      );
    }

    // For OpenRouter models, we want to display a more readable name
    let displayLabel = label;

    if (provider === "openrouter" && label.includes("/")) {
      // Extract the model name after the slash and format it nicely
      const modelPart = label.split("/")[1];
      // Keep only the first 3 parts to avoid overly long names
      const parts = modelPart.split("-").slice(0, 3);

      // Capitalize each part
      displayLabel = parts
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
    } else if (label.includes("/")) {
      // Handle other models with "/" that weren't caught by formatModelDisplayName
      // Extract just the model part after the slash
      displayLabel = label.split("/")[1];
    } else {
      // Apply the same date removal logic for regular model labels
      displayLabel = label;

      // Handle command models first to preserve variants like -a, -r7b
      // command-a-03-2025 -> command-a, command-r7b-12-2024 -> command-r7b
      displayLabel = displayLabel.replace(
        /(command-[a-z0-9]+)-\d{2}-\d{4}$/,
        "$1"
      );

      // Remove various date patterns from model names
      displayLabel = displayLabel.replace(/-\d{8}$/, "");
      displayLabel = displayLabel.replace(/-\d{4}-\d{2}-\d{2}$/, "");
      displayLabel = displayLabel.replace(/-\d{8}(?=-|$)/, "");
      displayLabel = displayLabel.replace(/-preview-\d{2}-\d{2}$/, "");
      displayLabel = displayLabel.replace(/-\d{4}\d{4}$/, "");

      // Remove general patterns like -03-2025, -12-2024 (MM-YYYY format) - but only if not already handled by command pattern
      displayLabel = displayLabel.replace(/-\d{2}-\d{4}$/, "");

      // If it's a model with "mini" in the name, shorten it (but preserve models like grok-3-mini-fast)
      if (
        displayLabel.includes("-mini-") &&
        !displayLabel.includes("-mini-fast")
      ) {
        displayLabel = displayLabel.split("-mini-")[0] + "-mini";
      }
    }

    return (
      <div className="flex items-center gap-2">
        {showIcon && <IconWrapper provider={provider} />}
        <span>{displayLabel}</span>
      </div>
    );
  };

  // Fix: Determine the current provider correctly, ensuring deepseek models use the deepseek provider
  const determineCurrentProvider = (model) => {
    if (!model) return "openai";

    // Check for OpenRouter models which use a slash format
    if (model.includes("/")) {
      const prefix = model.split("/")[0].toLowerCase();
      if (
        prefix === "cognitivecomputations" ||
        prefix === "nousresearch" ||
        prefix === "teknium" ||
        prefix === "migtissera" ||
        prefix === "liquid"
      ) {
        return "openrouter";
      }
    }

    // Check if model name includes specific provider names
    if (model.includes("deepseek")) return "deepseek";
    if (model.includes("gemini")) return "gemini";
    if (model.includes("claude")) return "anthropic";
    // Check for specific Groq models first before the general mistral check
    if (model === "mistral-large-2402") return "groq";
    if (model.includes("mistral") && !model.includes("mixtral"))
      return "mistral";
    if (model.includes("qwen")) return "qwen";
    if (model.includes("grok")) return "xai";
    if (model.includes("command")) return "cohere";
    if (model.includes("sonar") || model.includes("r1-1776"))
      return "perplexity";
    if (
      model.includes("gemma") ||
      model.includes("llama") ||
      model.includes("mixtral") ||
      (model.includes("mistral") && model.includes("mixtral")) ||
      model.includes("moonshotai") ||
      model.includes("kimi")
    )
      return "groq";

    // Fallback to MODEL_PROVIDERS or default to openai
    return MODEL_PROVIDERS[model] || "openai";
  };

  // Use optimistic model or fallback to current assistant model
  const displayModel = optimisticModel || effectiveAssistant?.model;
  const currentProvider = determineCurrentProvider(displayModel);
  const currentModelIsPremium = PREMIUM_MODELS.includes(displayModel || "");

  // Helper function to format model display names (shared between native and Radix select)
  const formatDisplayName = (modelName) => {
    if (!modelName) return "Select model";

    const displayNameMap = {
      "gemini-3-pro-preview": "gemini-3-pro",
      "moonshotai/kimi-k2-instruct-0905": "kimi-k2-instruct",
    };
    if (displayNameMap[modelName]) {
      return displayNameMap[modelName];
    }

    if (modelName.includes("/")) {
      const parts = modelName.split("/");
      if (parts.length === 2) {
        const modelPart = parts[1];
        if (modelPart.includes("hermes")) {
          return "Hermes " + modelPart.split("-").slice(-2, -1).join(" ");
        }
        return modelPart.split("-")[0];
      }
      return modelName.split("/")[1];
    }

    let cleanName = modelName;
    cleanName = cleanName.replace(/(command-[a-z0-9]+)-\d{2}-\d{4}$/, "$1");
    cleanName = cleanName.replace(/-\d{8}$/, "");
    cleanName = cleanName.replace(/-\d{4}-\d{2}-\d{2}$/, "");
    cleanName = cleanName.replace(/-\d{8}(?=-|$)/, "");
    cleanName = cleanName.replace(/-preview-\d{2}-\d{2}$/, "");
    cleanName = cleanName.replace(/-\d{4}\d{4}$/, "");
    cleanName = cleanName.replace(/-\d{2}-\d{4}$/, "");

    if (cleanName.includes("-mini-")) {
      cleanName = cleanName.split("-mini-")[0] + "-mini";
    }

    return cleanName;
  };

  // Native select handler for touch devices
  const handleNativeSelectChange = (e) => {
    const newValue = e.target.value;
    if (newValue) {
      handleModelChange(newValue);
    }
  };

  // Render native select for touch devices (iOS/Android)
  // This provides much better UX as it uses the native picker wheel
  if (isTouchDevice) {
    return (
      <div className={cn("relative", className)}>
        <div className="flex items-center gap-1.5 h-8 px-2">
          <IconWrapper provider={currentProvider} />
          <select
            value={displayModel || ""}
            onChange={handleNativeSelectChange}
            disabled={!user || isUpdating}
            className={cn(
              "appearance-none bg-transparent",
              "text-sm font-medium",
              "text-gray-900/90 dark:text-white/90",
              "border-none outline-none",
              "cursor-pointer",
              "pr-4", // Space for visual indicator
              "min-w-0 max-w-[140px]",
              "truncate",
              isUpdating && "opacity-75 cursor-not-allowed",
              !user && "opacity-75"
            )}
            style={{
              // Remove default iOS styling
              WebkitAppearance: "none",
              MozAppearance: "none",
            }}
          >
            {sortedModelOptions?.map((option) => {
              const isPremiumAndCantUse = option.isPremium && !isPremiumUser;
              const isDisabled =
                option.isDisabledInPreferences || isPremiumAndCantUse;

              return (
                <option
                  key={option.value}
                  value={option.value}
                  disabled={isDisabled}
                >
                  {formatDisplayName(option.label)}
                  {option.isPremium ? " ★" : ""}
                  {pinnedModels.includes(option.value) ? " 📌" : ""}
                </option>
              );
            })}
          </select>
          {currentModelIsPremium && (
            <Crown className="h-3.5 w-3.5 text-yellow-500 flex-shrink-0" />
          )}
        </div>
      </div>
    );
  }

  // Desktop: Use Radix UI Select with full styling
  return (
    <Select
      value={displayModel || ""}
      onValueChange={handleModelChange}
      disabled={!user || isUpdating}
      open={isSelectOpen}
      onOpenChange={handleOpenChange}
    >
      <SelectTrigger
        className={cn(
          "h-8 w-full min-w-0 px-2 rounded-md",
          "bg-transparent",
          "border border-transparent",
          "hover:bg-transparent",
          "transition-[background-color,opacity] duration-200",
          "data-[state=open]:border-transparent",
          "data-[state=closed]:border-transparent",
          "focus:outline-none",
          "focus-visible:outline-none",
          "focus:ring-0",
          "focus-visible:ring-0",
          "focus:ring-transparent",
          "focus-visible:ring-transparent",
          "focus:ring-offset-0",
          "focus-visible:ring-offset-0",
          "!ring-offset-transparent",
          "focus:border-transparent",
          "focus-visible:border-transparent",
          "data-[state=open]:ring-0",
          "data-[state=open]:ring-transparent",
          "data-[state=open]:ring-offset-0",
          "data-[state=open]:border-transparent",
          "data-[state=closed]:ring-0",
          "data-[state=closed]:ring-transparent",
          "data-[state=closed]:ring-offset-0",
          "data-[state=closed]:border-transparent",
          "relative group",

          "text-gray-900/90 dark:text-white/90",
          "[&>svg]:hidden", // Hide the default chevron icon
          isUpdating && "opacity-75 cursor-not-allowed",
          className
        )}
      >
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-1.5">
            <IconWrapper provider={currentProvider} />
            <CustomSelectValue
              value={displayModel || ""}
              placeholder="Select model"
              className={cn(
                "text-gray-900/90 dark:text-white/90",
                "font-medium",
                "placeholder:text-gray-600/60 dark:placeholder:text-white/60",
                !user && "opacity-75"
              )}
            />
          </div>

          {currentModelIsPremium && (
            <div className="flex-shrink-0">
              <Crown className="h-3.5 w-3.5 text-yellow-500" />
            </div>
          )}
        </div>
      </SelectTrigger>
      <SelectContent
        className={cn(
          "bg-white/80 dark:bg-zinc-900/80",
          "backdrop-blur-md",
          "border border-border/50",
          "w-[300px]",
          "rounded-2xl",
          "[&_svg]:text-purple-500", // Make check icons purple in dropdown
          // Prevent white border on focus/blur
          "focus:outline-none",
          "focus:ring-0",
          "focus:ring-transparent",
          "focus:border-border/50",
          "data-[state=open]:border-border/50",
          "data-[state=closed]:border-border/50"
        )}
        position="popper"
        sideOffset={4}
        style={{ zIndex: zIndex }}
      >
        {/* Use native overflow instead of ScrollArea for better mobile Safari touch support */}
        <div
          ref={scrollAreaRef}
          className="max-h-[300px] overflow-y-auto pr-2 scrollbar-thin"
        >
          {sortedModelOptions?.map((option, index) => {
            const isPinned = pinnedModels.includes(option.value);
            const prevWasPinned =
              index > 0 &&
              pinnedModels.includes(sortedModelOptions[index - 1].value);
            const showDivider = prevWasPinned && !isPinned;

            return (
              <div key={option.value}>
                {showDivider && (
                  <div className="border-t border-gray-200 dark:border-gray-700 my-1 mx-2" />
                )}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="group relative">
                        <SelectItem
                          value={option.value}
                          className={cn(
                            "text-foreground",
                            "hover-bg-adaptive",
                            "flex items-center gap-2 w-full",
                            "data-[highlighted]:bg-hover-light-lighter dark:data-[highlighted]:bg-hover-dark-lighter",
                            "data-[state=checked]:bg-hover-light-lighter dark:data-[state=checked]:bg-hover-dark-lighter",
                            "data-[state=checked]:font-medium",
                            "data-[state=checked]:[&_svg]:text-purple-500", // Make check icon purple when selected
                            // Remove white borders on focus
                            "focus:outline-none",
                            "focus:ring-0",
                            "focus:ring-transparent",
                            "focus:border-transparent",
                            "focus-visible:outline-none",
                            "focus-visible:ring-0",
                            "focus-visible:ring-transparent",
                            "focus-visible:border-transparent",
                            option.isDisabledInPreferences &&
                              "opacity-50 cursor-not-allowed",
                            option.isPremium &&
                              !isPremiumUser &&
                              "opacity-50 cursor-not-allowed"
                          )}
                          disabled={
                            option.isDisabledInPreferences ||
                            (option.isPremium && !isPremiumUser)
                          }
                        >
                          <div className="flex items-center justify-between w-full gap-2">
                            <div className="flex items-center gap-2">
                              <ModelLabel
                                label={option.label}
                                // Fix: Use the determineCurrentProvider function for option.value to ensure correct provider
                                provider={determineCurrentProvider(
                                  option.value
                                )}
                              />
                            </div>

                            <div className="flex items-center gap-1 ml-auto min-w-fit">
                              {/* Pinned indicator (read-only, pin/unpin in Settings > Models) */}
                              {isPinned && (
                                <TbPinFilled
                                  className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500"
                                  title="Pinned model (manage in Settings > Models)"
                                />
                              )}
                              {option.isPremium && (
                                <Crown className="h-4 w-4 text-yellow-500 flex-shrink-0 ml-1" />
                              )}
                              {option.isDisabledInPreferences && (
                                <span className="text-xs text-red-500 dark:text-red-400 ml-1">
                                  Disabled
                                </span>
                              )}
                            </div>
                          </div>
                        </SelectItem>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-xs z-[310]">
                      {option.isDisabledInPreferences ? (
                        <div className="space-y-2">
                          <p className="font-medium text-red-500">
                            Model Disabled
                          </p>
                          <p className="text-sm text-muted-foreground">
                            This model is disabled in your preferences. Go to
                            Settings → Models to enable it.
                          </p>
                        </div>
                      ) : option.isPremium && !isPremiumUser ? (
                        <div className="space-y-2">
                          <p className="font-medium text-yellow-500">
                            Premium Model
                          </p>
                          <p className="text-sm text-muted-foreground">
                            This model requires a premium subscription to use.
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <p className="font-medium">
                              {getModelDisplayName(option.label)}
                            </p>
                            <span className="text-xs text-muted-foreground ml-2">
                              {determineCurrentProvider(option.value)
                                .charAt(0)
                                .toUpperCase() +
                                determineCurrentProvider(option.value).slice(1)}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {MODEL_CAPABILITIES[option.value] ||
                              MODEL_CAPABILITIES["default"]}
                          </p>
                        </div>
                      )}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            );
          })}
        </div>
      </SelectContent>
    </Select>
  );
};

ModelSwitcher.propTypes = {
  className: PropTypes.string,
  zIndex: PropTypes.number,
};

export default ModelSwitcher;
